import { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { Skeleton } from "primereact/skeleton";
import { useNavigate } from "react-router-dom";
import { useMountEffect } from "primereact/hooks";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import * as Displayhelper from "../../../../helpers/DisplayHelper";
import { AccessScopeConstants, GlobalConstants } from "../../../../constants";
import {
  userDetailsRequest,
  updateUserDetailRequest,
  userPendingAssignmentRequest,
  markAssignmentAsCompletedRequest,
} from "../../../../store/slices/usersSlice";
import {
  departmentRequest,
  designationRequest,
  rolesRequest,
} from "../../../../store/slices/metaSlice";
import { listRequest as roleListRequest } from "../../../../store/slices/rolesSlice";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import AvatarWithProgress from "../../../common/avatarWithProgress";
import { Message } from "primereact/message";
import { TabMenu } from "primereact/tabmenu";
import DetailsCard from "../../../common/detailsCard";
import { useManagerPicker } from "./useManagerPicker";
import * as ListCellRenders from "../../../common/list/ListCellRenders";
import MarkCompletedDialog from "./MarkCompletedDialog";

import "./user-details.scss";
import List from "../../../common/list";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import {
  isEmailOptional,
  isSsoSetUp,
  USER_TYPE_OPTIONS,
  USER_TYPE_SSO,
} from "../../../../helpers/SsoHelper";
import UserMfaCard from "./UserMfaCard";
import UserWelcomeEmailCard from "./UserWelcomeEmailCard";

export const UserDetails = ({
  action,
  departmentRequest,
  departments,
  designationRequest,
  designations,
  enterprise,
  error,
  labels,
  record,
  requesting,
  roleListRequest,
  rolesRequest,
  updateUserDetailRequest,
  user,
  userDetailsRequest,
  roles,
  userPendingAssignmentRequest,
  markAssignmentAsCompletedRequest,
}) => {
  const intl = useIntl();
  const managerPicker = useManagerPicker(record);
  const navigate = useNavigate();
  const params = useParams();
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  // The pending assignment whose Mark As Completed dialog is open.
  const [markingAssignment, setMarkingAssignment] = useState(null);
  // The server's refusal of this opening's completion. Kept here because the
  // store moves on to the pending list refetch straight after the reply.
  const [markError, setMarkError] = useState(null);
  const wasMarking = useRef(false);

  useMountEffect(() => {
    userDetailsRequest({ id: params.id });
    departmentRequest();
    designationRequest();
    roleListRequest({
      pageSize: 500,
      pageNo: 1,
    });
    rolesRequest();
  });

  const { role } = user || {};
  const canUpdate = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.UserAccess,
    AccessScopeConstants.UpdateScope
  );

  // The user type shows once the organisation has company sign-in, and
  // always for an SSO user.
  const showsUserType =
    isSsoSetUp(enterprise) || record?.profileType === USER_TYPE_SSO;

  const calculateProfileCompletion = useCallback(() => {
    let totalFields = 4 + 4;
    record?.sections.forEach((section) => {
      totalFields += section.fields?.length;
    });
    let completedFields = 4;
    if (record?.designation) completedFields++;
    if (record?.department) completedFields++;
    if (record?.managerId) completedFields++;
    if (record?.phoneNumber) completedFields++;

    record?.sections.forEach((section) => {
      completedFields += section.fields?.filter((field) => field.value).length;
    });
    setProfileCompletion(Math.floor((completedFields / totalFields) * 100));
  }, [record]);

  // Helper function to map numeric field types to string types
  const getFieldTypeString = useCallback((numericType) => {
    const fieldType = GlobalConstants.FIELD_TYPES.find(
      (type) => type.value === numericType
    );
    return fieldType ? fieldType.name : "text";
  }, []);

  useEffect(() => {
    if (record) {
      calculateProfileCompletion();
    }
  }, [record, calculateProfileCompletion]);

  const handleOnSubmit = (section, data) => {
    if (section !== "custom") {
      updateUserDetailRequest({
        id: record?.id,
        detailType: section,
        ...data,
      });
    } else {
      updateUserDetailRequest({
        id: record?.id,
        detailType: "dynamic_details",
        details: data,
      });
    }
  };

  useEffect(() => {
    if (action === "update" && !requesting && !error) {
      userDetailsRequest({ id: params.id });
      departmentRequest();
      designationRequest();
    } else if (action === "mark-as-completed" && !requesting) {
      userPendingAssignmentRequest({ id: params.id });
    }
  }, [error, requesting, action]);

  useEffect(() => {
    if (activeTab === 1) {
      userPendingAssignmentRequest({ id: params.id });
    }
  }, [activeTab]);

  // The dialog closes once its completion is saved; on a refusal it stays
  // open and says why.
  const marking = action === "mark-as-completed" && requesting;
  useEffect(() => {
    if (wasMarking.current && !marking) {
      if (error) {
        setMarkError(error);
      } else {
        setMarkingAssignment(null);
      }
    }
    wasMarking.current = marking;
  }, [marking, error]);

  return (
    <div>
      <ConfirmDialog />
      <CustomBreadCrumbs
        labels={labels}
        navigate={navigate}
        editLabel={record?.email || record?.username}
      />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({ id: "user_details" })}
          </div>
        </div>
        <div
          className="edexlms-page-body user-details"
          style={{
            height: "calc(100vh - 64px - 20px - 45px - 40px - 14px - 40px)",
            overflowY: "auto",
          }}
        >
          {(record && (
            <div className="edexlms-user-banner">
              <div className="edexlms-user-basic-details flex justify-content-between align-items-center">
                <div className="edexlms-user-image flex flex-column gap-2 justify-content-center align-items-center">
                  <AvatarWithProgress
                    label={Displayhelper.getUserShortName(
                      record?.name ||
                        `${record?.firstName}${
                          record?.lastName ? ` ${record?.lastName}` : ""
                        }`
                    )}
                    completion={profileCompletion}
                    radius={54}
                  />
                  <div className="edexlms-profile-completion">
                    <>
                      {intl.formatMessage(
                        { id: "profile_completion" },
                        {
                          percentage: profileCompletion,
                          b: (chunks) => (
                            <span className="text-lg font-bold">{chunks}</span>
                          ),
                        }
                      )}
                    </>
                  </div>
                </div>
                <div className="edexlms-user-details flex flex-column gap-2">
                  <div className="flex justify-content-center align-items-center">
                    <div className="edexlms-user-name font-bold text-xl">
                      {record?.name ||
                        `${record?.firstName}${
                          record?.lastName ? ` ${record?.lastName}` : ""
                        }`}
                    </div>
                    <Message
                      severity={record?.isActive ? "success" : "error"}
                      text={intl.formatMessage({
                        id: record?.isActive ? "active" : "inactive",
                      })}
                      style={{ marginLeft: "10px", padding: "7px" }}
                    />
                  </div>
                  <div className="flex justify-content-center align-items-center gap-3">
                    <i className="pi pi-briefcase text-muted"></i>
                    <div className="edexlms-user-email text-muted">
                      {record?.designation || "-"}
                    </div>
                  </div>
                  <div className="flex justify-content-center align-items-center gap-3">
                    <i className="pi pi-envelope text-muted"></i>
                    <div className="edexlms-user-email text-muted">
                      {record?.email || "-"}
                    </div>
                  </div>
                  <div className="flex justify-content-center align-items-center gap-3">
                    <i className="pi pi-mobile text-muted"></i>
                    <div className="edexlms-user-email text-muted">
                      {record?.phoneNumber || "-"}
                    </div>
                  </div>
                </div>
              </div>
              <div className="edexlms-user-batches flex-grow-1"></div>
            </div>
          )) || <Skeleton width="100%" height="169px"></Skeleton>}
          <TabMenu
            className="edexlms-tab-menu"
            model={[
              {
                label: intl.formatMessage({ id: "profile" }),
                command: () => {
                  if (activeTab !== 0) {
                    setActiveTab(0);
                  }
                },
              },
              {
                label: intl.formatMessage({ id: "pending_assignments" }),
                command: () => {
                  if (activeTab !== 1) {
                    setActiveTab(1);
                  }
                },
              },
            ]}
          />
          {(activeTab === 0 && (
            <div className="edexlms-tab-content">
              <div
                style={{
                  marginBottom: "10px",
                  fontStyle: "italic",
                  fontSize: 12,
                }}
                className="flex gap-2 text-muted"
              >
                * Personal Details, Work Details, System Permission are system
                defined sections.
              </div>
              <ResponsiveMasonry
                style={{ paddingRight: "10px" }}
                columnsCountBreakPoints={{ 350: 1, 900: 2 }}
                gutterBreakpoints={{ 350: "12px", 900: "16px" }}
              >
                <Masonry>
                  <DetailsCard
                    canUpdate={canUpdate}
                    message={intl.formatMessage({
                      id: "personal_details_message",
                    })}
                    // The card has no phone field (taken off on purpose),
                    // but the API stores every personal detail, null for one
                    // left out, so the stored phone number goes with the save
                    // to keep it (F-BUG-1). A cleared field comes back
                    // undefined; an SSO user's email may be blank while
                    // people are matched by username.
                    onSubmit={(data) =>
                      handleOnSubmit("personal_details", {
                        ...data,
                        email: data.email || "",
                        phoneNumber: record?.phoneNumber,
                      })
                    }
                    style={{ flex: 1 }}
                    details={record}
                    fields={[
                      {
                        label: "firstName",
                        value: record?.firstName,
                        isRequired: true,
                        type: "text",
                        i18n: true,
                      },
                      {
                        label: "lastName",
                        value: record?.lastName,
                        isRequired: true,
                        type: "text",
                        i18n: true,
                      },
                      {
                        label: "email",
                        value: record?.email,
                        isRequired: !isEmailOptional(enterprise, record?.profileType),
                        type: "text",
                        i18n: true,
                      },
                      {
                        label: "username",
                        value: record?.username,
                        isRequired: true,
                        type: "text",
                        i18n: true,
                        readOnly: true,
                      },
                    ]}
                    title={intl.formatMessage({ id: "personal_details" })}
                  />
                  <DetailsCard
                    onSubmit={(data) =>
                      handleOnSubmit("official_details", data)
                    }
                    message={intl.formatMessage({
                      id: "official_details_message",
                    })}
                    canUpdate={canUpdate}
                    style={{ flex: 1 }}
                    details={record}
                    title={intl.formatMessage({ id: "official_details" })}
                    fields={[
                      {
                        label: "managerId",
                        value: record?.managerId,
                        type: "dropdown",
                        // Searches the server; keeps the current manager (E2E26)
                        options: managerPicker.options,
                        filter: true,
                        onFilter: managerPicker.onFilter,
                        i18n: true,
                      },
                      {
                        label: "designation",
                        value: record?.designation,
                        type: "dropdown",
                        editable: true,
                        options: designations.map((designation) => ({
                          name: designation.value,
                          value: designation.value,
                          id: designation.id,
                        })),
                        i18n: true,
                      },
                      {
                        label: "department",
                        value: record?.department,
                        type: "dropdown",
                        editable: true,
                        options: departments.map((department) => ({
                          name: department.value,
                          value: department.value,
                          id: department.id,
                        })),
                        i18n: true,
                      },
                    ]}
                  />
                  <DetailsCard
                    onSubmit={(data) => handleOnSubmit("role_details", data)}
                    canUpdate={canUpdate && record?.id !== user?.id}
                    style={{ flex: 1 }}
                    details={record}
                    title={intl.formatMessage({ id: "role_details" })}
                    fields={[
                      {
                        label: "role",
                        name: "roleId",
                        value: record?.roleId,
                        isRequired: true,
                        type: "dropdown",
                        options: roles.map((role) => ({
                          name: role.name,
                          value: role.id,
                          id: role.id,
                        })),
                        i18n: true,
                      },
                    ]}
                  />
                  {(showsUserType && (
                    <DetailsCard
                      onSubmit={(data) =>
                        handleOnSubmit("user_type", {
                          profileType: Number(data.profileType),
                        })
                      }
                      canUpdate={canUpdate && record?.id !== user?.id}
                      style={{ flex: 1 }}
                      details={record}
                      title={intl.formatMessage({ id: "sign_in_details" })}
                      fields={[
                        {
                          label: "user_type",
                          name: "profileType",
                          value: record?.profileType,
                          isRequired: true,
                          type: "dropdown",
                          options: USER_TYPE_OPTIONS,
                          i18n: true,
                        },
                      ]}
                    />
                  )) ||
                    null}
                  {((record?.hasMfa || record?.mfaRequired) && (
                    <UserMfaCard
                      record={record}
                      canUpdate={canUpdate}
                      sysRootOnly={
                        AccessScopeConstants.isSysRootRole(record?.roleId) &&
                        !AccessScopeConstants.isSysRootRole(role?.id)
                      }
                      onReset={() => userDetailsRequest({ id: params.id })}
                    />
                  )) ||
                    null}
                  {(record?.awaitingFirstSignIn &&
                    record?.isActive &&
                    record?.email && (
                      <UserWelcomeEmailCard
                        record={record}
                        canUpdate={canUpdate}
                      />
                    )) ||
                    null}
                  {record?.sections
                    ?.filter((section) => section.fields.length)
                    .map((section) => (
                      <DetailsCard
                        key={section.name}
                        onSubmit={(data) => handleOnSubmit("custom", data)}
                        canUpdate={canUpdate}
                        style={{ flex: 1 }}
                        details={record}
                        title={section.name}
                        fields={section.fields.map((field) => ({
                          label: field.label,
                          name: field.id,
                          value: field.value,
                          isRequired: field.isRequired,
                          type: field.type,
                          maxLength: field.maxLength,
                          placeholder: field.placeholder,
                          options: field.options?.map((option) => ({
                            name: option,
                            id: option,
                            value: option,
                          })),
                        }))}
                      />
                    ))}
                </Masonry>
              </ResponsiveMasonry>
            </div>
          )) ||
            null}
          {activeTab === 1 && (
            <List
              scrollHeight="calc(100vh - 460px)"
              data={record?.pendingAssignments || []}
              pagination={false}
              selectionMode=""
              columns={[
                {
                  field: "learningObjectName",
                  header: "learning_object",
                  sortable: true,
                },
                {
                  field: "learningMilestoneName",
                  header: "learning_milestone",
                  sortable: true,
                },
                {
                  field: "learningProgramName",
                  header: "learning_program",
                  sortable: true,
                  body: ListCellRenders.NameWithOptionalTag(
                    "learningProgramName"
                  ),
                },
                {
                  field: "sessionTime",
                  header: "session_time_mins",
                  sortable: true,
                  body: (data) => {
                    return (
                      (data?.sessionTime &&
                        Displayhelper.convertSeconds(data?.sessionTime || 0)) ||
                      "-"
                    );
                  },
                },
                {
                  field: "status",
                  header: "status",
                  body: ListCellRenders.LearningStatus("status"),
                },
                {
                  field: "actions",
                  headerIcon: "pi-cog",
                  align: "center",
                  body: (rowData) => {
                    return (
                      <Button
                        disabled={!canUpdate}
                        outlined
                        onClick={() => {
                          if (canUpdate) {
                            setMarkError(null);
                            setMarkingAssignment(rowData);
                          }
                        }}
                        tooltip={
                          canUpdate
                            ? ""
                            : intl.formatMessage({ id: "no_permission" })
                        }
                      >
                        {intl.formatMessage({ id: "mark_as_completed" })}
                      </Button>
                    );
                  },
                },
              ]}
            />
          )}
          <MarkCompletedDialog
            assignment={markingAssignment}
            requesting={marking}
            error={markError}
            onHide={() => setMarkingAssignment(null)}
            onSubmit={(completionDate) => {
              setMarkError(null);
              markAssignmentAsCompletedRequest({
                userId: params.id,
                id: markingAssignment.id,
                completionDate,
              });
            }}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  record: state.users.record,
  designations: state.meta?.designations,
  departments: state.meta?.departments,
  user: state.self?.user,
  action: state.users.action,
  error: state.users.error,
  requesting: state.users.requesting,
  roles: state.meta?.roles,
  enterprise: state.global?.enterprise,
});

const mapDispatchToProps = (dispatch) => ({
  userDetailsRequest: (id) => dispatch(userDetailsRequest(id)),
  departmentRequest: () => dispatch(departmentRequest()),
  designationRequest: () => dispatch(designationRequest()),
  roleListRequest: (data) => dispatch(roleListRequest(data)),
  updateUserDetailRequest: (data) => dispatch(updateUserDetailRequest(data)),
  rolesRequest: () => dispatch(rolesRequest()),
  userPendingAssignmentRequest: (data) =>
    dispatch(userPendingAssignmentRequest(data)),
  markAssignmentAsCompletedRequest: (data) =>
    dispatch(markAssignmentAsCompletedRequest(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserDetails);
